import React, { useState, useEffect } from 'react';
import { adminApi, AdminUserDto, AdminUserDetailDto, AdminUpdateUserDto, AdminCreateUserDto } from '../services/adminApi';
import { adminPromotionPackagesApi, PromotionPackageDto } from '../services/adminApi';
import { toast } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EllipsisVerticalIcon,
  UserPlusIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserDetailDto | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUpdateUserDto | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [newUser, setNewUser] = useState<AdminCreateUserDto>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    isActive: true,
    isVerified: false,
    isEmailVerified: false,
    isPhoneVerified: false,
    city: '',
    province: '',
    postalCode: '',
    businessName: '',
    businessDescription: '',
    businessWebsite: '',
    userType: 'Individual'
  });
  const [userStats, setUserStats] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [promotionPackages, setPromotionPackages] = useState<PromotionPackageDto[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
    fetchPromotionPackages();
  }, [currentPage, searchTerm]);

  const fetchPromotionPackages = async () => {
    try {
      const response = await adminPromotionPackagesApi.getAll();
      setPromotionPackages(response.data);
    } catch (error) {
      console.error('Error fetching promotion packages:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsers(currentPage, 20, searchTerm);
      
      // Users data loaded successfully
      
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      toast.error('خطا در بارگذاری کاربران', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await adminApi.getUserStats();
      setUserStats(response.data);
    } catch (error) {
      // Remove console.error - stats loading failure is not critical
    }
  };

  const viewUserDetail = async (userId: string) => {
    try {
      const response = await adminApi.getUser(userId);
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('خطا در بارگذاری جزئیات کاربر', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    }
  };

  const startEditUser = (user: AdminUserDto) => {
    setEditingUser({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '',
      phoneNumber: user.phoneNumber,
      isActive: user.isActive,
      isVerified: user.isVerified,
      isEmailVerified: user.isEmailVerified,
      isPhoneVerified: user.isPhoneVerified,
      city: user.city,
      province: user.province,
      postalCode: '',
      businessName: user.businessName,
      businessDescription: '',
      businessWebsite: '',
      userType: user.userType
    });
    setEditingUserId(user.id);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser || !editingUserId) return;

    setActionLoading('update');
    try {
      await adminApi.updateUser(editingUserId, editingUser);
      toast.success('کاربر با موفقیت به‌روزرسانی شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      setShowEditModal(false);
      setEditingUserId(null);
      fetchUsers();
    } catch (error) {
      toast.error('خطا در به‌روزرسانی کاربر', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddPlan = async () => {
    if (!selectedUser || !selectedPackageId) return;

    setActionLoading('add-plan');
    try {
      await adminApi.createUserPlan(selectedUser.id, selectedPackageId);
      toast.success('پلن با موفقیت اضافه شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          padding: '16px',
        }
      });
      setSelectedPackageId('');
      setActionLoading(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'خطا در افزودن پلن', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          padding: '16px',
        }
      });
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                          <span className="font-medium">حذف کاربر</span>
        </div>
                  <p className="text-sm text-gray-600">آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            لغو
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await adminApi.deleteUser(userId);
                toast.success('کاربر با موفقیت حذف شد', {
                  icon: '🗑️',
                  style: {
                    background: '#10B981',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                });
                fetchUsers();
              } catch (error) {
                toast.error('خطا در حذف کاربر', {
                  icon: '❌',
                  style: {
                    background: '#EF4444',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                });
              }
            }}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
          >
                            حذف
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: 'white',
        color: 'black',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        borderRadius: '0.75rem',
        padding: '1rem',
      }
    });
  };

  const handleVerifyUser = async (userId: string) => {
    setActionLoading(`verify-${userId}`);
    try {
      await adminApi.verifyUser(userId);
              toast.success('کاربر با موفقیت تأیید شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      fetchUsers();
    } catch (error) {
              toast.error('خطا در تأیید کاربر', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    let suspensionReason = '';
    
    toast((t) => (
      <div className="flex flex-col gap-3 min-w-80">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                      <span className="font-medium">تعلیق کاربر</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            دلیل تعلیق حساب:
          </label>
          <textarea
            onChange={(e) => suspensionReason = e.target.value}
                          placeholder="دلیل تعلیق را وارد کنید..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={3}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            لغو
          </button>
          <button
            onClick={async () => {
              if (!suspensionReason.trim()) {
                toast.error('لطفاً دلیل تعلیق را وارد کنید', {
                  icon: '⚠️',
                  style: {
                    background: '#F59E0B',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                });
                return;
              }
              toast.dismiss(t.id);
              try {
                await adminApi.suspendUser(userId, suspensionReason);
                toast.success('کاربر با موفقیت تعلیق شد', {
                  icon: '🚫',
                  style: {
                    background: '#10B981',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                });
                fetchUsers();
              } catch (error) {
                toast.error('خطا در تعلیق کاربر', {
                  icon: '❌',
                  style: {
                    background: '#EF4444',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                });
              }
            }}
            className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
                          تعلیق
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: 'white',
        color: 'black',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        borderRadius: '0.75rem',
        padding: '1rem',
      }
    });
  };

  const handleChangeUserRole = async (userId: string, newRole: string, currentRole: string) => {
    if (currentRole === newRole) return;

    // Show confirmation dialog
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
                      <span className="font-medium">تغییر نقش کاربر</span>
        </div>
        <p className="text-sm text-gray-600">
                      آیا مطمئن هستید که می‌خواهید نقش این کاربر را از "{getRoleName(currentRole)}" به "{getRoleName(newRole)}" تغییر دهید؟
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            لغو
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              executeRoleChange(userId, newRole);
            }}
            className="px-3 py-1 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600"
          >
            تأیید
          </button>
        </div>
      </div>
    ), {
      duration: 0,
      style: {
        background: '#ffffff',
        color: '#000000',
        fontFamily: 'iranSans, sans-serif',
        textAlign: 'right',
        direction: 'rtl',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: '500',
        padding: '16px',
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      }
    });
  };

  const executeRoleChange = async (userId: string, newRole: string) => {
    setActionLoading(`role-${userId}`);
    try {
      // Find the user to update
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // Create update payload with new role
      const updateData: AdminUpdateUserDto = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        isActive: user.isActive,
        isVerified: user.isVerified,
        isEmailVerified: user.isEmailVerified,
        isPhoneVerified: user.isPhoneVerified,
        city: user.city,
        province: user.province,
        postalCode: '',
        businessName: user.businessName,
        businessDescription: '',
        businessWebsite: '',
        userType: newRole
      };

      await adminApi.updateUser(userId, updateData);
      
              toast.success(`نقش کاربر با موفقیت به "${getRoleName(newRole)}" تغییر کرد`, {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      
      fetchUsers(); // Refresh the users list
    } catch (error) {
              toast.error('خطا در تغییر نقش کاربر', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleName = (role: string) => {
    switch (role) {
          case 'Individual': return 'شخصی';
    case 'Business': return 'تجاری';
    case 'Admin': return 'ادمین';
      default: return role;
    }
  };

  const getStatusBadge = (user: AdminUserDto) => {
    if (!user.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          <XCircleIcon className="w-3 h-3" />
          غیرفعال
        </span>
      );
    }
    if (user.isVerified) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircleIcon className="w-3 h-3" />
          تأیید شده
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
        <ExclamationTriangleIcon className="w-3 h-3" />
        در انتظار تأیید
      </span>
    );
  };

  const getUserTypeBadge = (userType: string) => {
    switch (userType) {
      case 'Business':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
            🏢 تجاری
          </span>
        );
      case 'Admin':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            👑 ادمین
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
            👤 شخصی
          </span>
        );
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && user.isActive) ||
      (filterStatus === 'inactive' && !user.isActive) ||
      (filterStatus === 'verified' && user.isVerified) ||
      (filterStatus === 'unverified' && !user.isVerified);
    
    const matchesType = filterType === 'all' || 
      (filterType === 'individual' && user.userType === 'Individual') ||
      (filterType === 'business' && user.userType === 'Business') ||
      (filterType === 'admin' && user.userType === 'Admin');
    
    return matchesStatus && matchesType;
  });

  const handleCreateNewUser = () => {
    setShowCreateModal(true);
  };

  const handleCreateUser = async () => {
    setActionLoading('create');
    try {
      await adminApi.createUser(newUser);
              toast.success('کاربر جدید با موفقیت ایجاد شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      setShowCreateModal(false);
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        isActive: true,
        isVerified: false,
        isEmailVerified: false,
        isPhoneVerified: false,
        city: '',
        province: '',
        postalCode: '',
        businessName: '',
        businessDescription: '',
        businessWebsite: '',
        userType: 'Individual'
      });
      fetchUsers();
    } catch (error) {
              toast.error('خطا در ایجاد کاربر جدید', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportUsers = async () => {
    try {
      setActionLoading('export');
      // Create CSV content
      const csvContent = generateUsersCsv(users);
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('فایل کاربران با موفقیت دانلود شد', {
        icon: '📥',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    } catch (error) {
      toast.error('خطا در دانلود فایل', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const generateUsersCsv = (users: AdminUserDto[]) => {
    const headers = [
      'نام',
      'نام خانوادگی', 
      'ایمیل',
      'شماره تلفن',
      'نوع حساب',
      'وضعیت',
      'تاریخ عضویت',
      'شهر',
      'استان',
      'کل آگهی‌ها',
      'آگهی‌های فعال'
    ];
    
    const csvRows = [headers.join(',')];
    
    for (const user of users) {
      const row = [
        user.firstName,
        user.lastName,
        user.email,
        user.phoneNumber || '',
        user.userType === 'Individual' ? 'شخصی' : 'تجاری',
        user.isActive ? 'فعال' : 'غیرفعال',
        new Date(user.dateJoined).toLocaleDateString('fa-IR'),
        user.city || '',
        user.province || '',
        user.totalAds || 0,
        user.activeAds || 0
      ];
      csvRows.push(row.join(','));
    }
    
    return csvRows.join('\n');
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">کاربران</h1>
              <p className="mt-1 text-sm text-gray-500">
                                  مدیریت تمام کاربران ثبت‌نام شده در سیستم
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={handleExportUsers}
                disabled={actionLoading === 'export'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {actionLoading === 'export' ? 'در حال دانلود...' : 'خروجی'}
              </button>
              <button 
                onClick={handleCreateNewUser}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <UserPlusIcon className="w-4 h-4" />
                کاربر جدید
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        {userStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div key="total-users" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">کل کاربران</p>
                  <p className="text-2xl font-semibold text-gray-900">{(userStats.totalUsers || 0).toLocaleString('fa-IR')}</p>
                                      <p className="text-xs text-green-600 font-medium">+۱۲٪ نسبت به ماه گذشته</p>
                </div>
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">👥</span>
                </div>
              </div>
            </div>
            
            <div key="verified-users" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">تأیید شده</p>
                  <p className="text-2xl font-semibold text-gray-900">{(userStats.verifiedUsers || 0).toLocaleString('fa-IR')}</p>
                                      <p className="text-xs text-green-600 font-medium">+۸٪ نسبت به ماه گذشته</p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">✅</span>
                </div>
              </div>
            </div>
            
            <div key="business-users" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">تجاری</p>
                  <p className="text-2xl font-semibold text-gray-900">{(userStats.businessUsers || 0).toLocaleString('fa-IR')}</p>
                                      <p className="text-xs text-primary-600 font-medium">+۱۵٪ نسبت به ماه گذشته</p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🏢</span>
                </div>
              </div>
            </div>
            
            <div key="new-users" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">این ماه</p>
                  <p className="text-2xl font-semibold text-gray-900">{(userStats.newUsersThisMonth || 0).toLocaleString('fa-IR')}</p>
                                      <p className="text-xs text-green-600 font-medium">+۲۲٪ نسبت به ماه گذشته</p>
                </div>
                <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📈</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Search and Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="جستجو بر اساس نام، ایمیل یا شماره تلفن..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-11 pl-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>
            
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[120px]"
              >
                <option value="all">همه وضعیت‌ها</option>
                <option value="active">فعال</option>
                <option value="inactive">غیرفعال</option>
                <option value="verified">تأیید شده</option>
                <option value="unverified">تأیید نشده</option>
              </select>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[120px]"
              >
                <option value="all">همه انواع</option>
                <option value="individual">شخصی</option>
                <option value="business">تجاری</option>
                <option value="admin">ادمین</option>
              </select>
              
              <button className="inline-flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                <FunnelIcon className="w-4 h-4" />
                فیلترها
              </button>
            </div>
          </div>
        </div>

        {/* Modern Users Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                لیست کاربران ({filteredUsers.length} کاربر)
              </h3>
              <div className="text-sm text-gray-500">
                صفحه {currentPage} از {pagination?.totalPages || 1}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">در حال بارگذاری کاربران...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          نام کاربر
                        </th>
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          اطلاعات تماس
                        </th>
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          نوع حساب / نقش
                        </th>
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          وضعیت
                        </th>
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          آگهی‌ها
                        </th>
                                              <th className="text-right py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                          تاریخ عضویت
                        </th>
                      <th className="text-center py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        عملیات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user, index) => (
                      <tr key={user.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-10 h-10">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                                {user.firstName[0]}{user.lastName[0]}
                              </div>
                            </div>
                            <div className="mr-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{user.phoneNumber || 'ندارد'}</div>
                          <div className="text-sm text-gray-500">{user.city || ''}, {user.province || ''}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-2">
                            {getUserTypeBadge(user.userType)}
                            <select
                              value={user.userType}
                              onChange={(e) => handleChangeUserRole(user.id, e.target.value, user.userType)}
                              disabled={actionLoading === `role-${user.id}`}
                              className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
                            >
                                              <option value="Individual">شخصی</option>
                <option value="Business">تجاری</option>
                <option value="Admin">ادمین</option>
                            </select>
                            {actionLoading === `role-${user.id}` && (
                              <div className="flex items-center gap-1 text-xs text-orange-600">
                                <div className="w-3 h-3 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                                <span>در حال تغییر...</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(user)}
                        </td>
                        <td className="px-6 py-4">
                                          <div className="text-sm text-gray-900">کل: {user.totalAds || 0}</div>
                <div className="text-sm text-gray-500">فعال: {user.activeAds || 0}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(user.dateJoined).toLocaleDateString('fa-IR')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => viewUserDetail(user.id)}
                              className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="مشاهده جزئیات"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditUser(user)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="ویرایش"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            {!user.isVerified && (
                              <button
                                onClick={() => handleVerifyUser(user.id)}
                                disabled={actionLoading === `verify-${user.id}`}
                                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="تأیید کاربر"
                              >
                                {actionLoading === `verify-${user.id}` ? (
                                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <CheckCircleIcon className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {pagination && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                                    نمایش <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> تا{' '}
              <span className="font-medium">{Math.min(currentPage * 20, pagination.totalUsers || 0)}</span> از{' '}
              <span className="font-medium">{pagination.totalUsers || 0}</span> کاربر
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                                                  قبلی
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, pagination.totalPages || 1))].map((_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(pagination.totalPages || 1, currentPage + 1))}
                        disabled={currentPage === (pagination.totalPages || 1)}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                                                  بعدی
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Enhanced User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4" dir="rtl">
            <div className="relative top-8 mx-auto max-w-4xl">
              <div className="bg-white rounded-xl shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                                  <h3 className="text-xl font-semibold text-gray-900">جزئیات کاربر</h3>
              <p className="text-sm text-gray-500 mt-1">اطلاعات کامل برای {selectedUser.firstName} {selectedUser.lastName}</p>
                  </div>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">اطلاعات شخصی</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">نام</label>
                          <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.firstName}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
                          <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.lastName}</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ایمیل</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.email}</p>
                      </div>
                      
                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">شماره تلفن</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.phoneNumber || 'ندارد'}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">شهر</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.city || 'ندارد'}</p>
                        </div>
                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">استان</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedUser.province || 'ندارد'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Account Information */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">اطلاعات حساب</h4>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">نوع حساب:</span>
                          {getUserTypeBadge(selectedUser.userType)}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">وضعیت:</span>
                          {getStatusBadge(selectedUser)}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">تاریخ عضویت:</span>
                                                      <span className="text-sm text-gray-900">{new Date(selectedUser.dateJoined).toLocaleDateString('fa-IR')}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">کل آگهی‌ها:</span>
                          <span className="text-sm text-gray-900">{selectedUser.totalAds || 0}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">آگهی‌های فعال:</span>
                          <span className="text-sm text-gray-900">{selectedUser.activeAds || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add Plan Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">اختصاص پلن</h4>
                    <div className="flex items-center gap-3">
                      <select
                        value={selectedPackageId}
                        onChange={(e) => setSelectedPackageId(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">انتخاب پلن...</option>
                        {promotionPackages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} - ${pkg.price} ({pkg.durationDays} روز)
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleAddPlan}
                        disabled={!selectedPackageId || actionLoading === 'add-plan'}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionLoading === 'add-plan' ? 'در حال افزودن...' : 'افزودن پلن'}
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowUserModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      بستن
                    </button>
                    <button
                      onClick={() => {
                        startEditUser(selectedUser);
                        setShowUserModal(false);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      ویرایش کاربر
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4" dir="rtl">
            <div className="relative top-8 mx-auto max-w-2xl">
              <div className="bg-white rounded-xl shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                                  <h3 className="text-xl font-semibold text-gray-900">ویرایش کاربر</h3>
              <p className="text-sm text-gray-500 mt-1">ویرایش اطلاعات کاربر</p>
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                        <input
                          type="text"
                          value={editingUser.firstName}
                          onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام خانوادگی</label>
                        <input
                          type="text"
                          value={editingUser.lastName}
                          onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                        <input
                          type="email"
                          value={editingUser.email}
                          onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور</label>
                        <input
                          type="password"
                          value={editingUser.password || ''}
                          onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="خالی بگذارید تا رمز عبور فعلی حفظ شود"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                        <input
                          type="text"
                          value={editingUser.phoneNumber || ''}
                          onChange={(e) => setEditingUser({...editingUser, phoneNumber: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">شهر</label>
                        <input
                          type="text"
                          value={editingUser.city || ''}
                          onChange={(e) => setEditingUser({...editingUser, city: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">استان</label>
                        <input
                          type="text"
                          value={editingUser.province || ''}
                          onChange={(e) => setEditingUser({...editingUser, province: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    {/* User Role */}
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">نقش کاربر</h4>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نوع حساب / نقش</label>
                        <select
                          value={editingUser.userType}
                          onChange={(e) => setEditingUser({...editingUser, userType: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="Individual">👤 شخصی</option>
                          <option value="Business">🏢 تجاری</option>
                          <option value="Admin">👑 ادمین</option>
                        </select>
                      </div>
                    </div>

                    {/* Status Checkboxes */}
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">وضعیت حساب</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingUser.isActive}
                            onChange={(e) => setEditingUser({...editingUser, isActive: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">فعال</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingUser.isVerified}
                            onChange={(e) => setEditingUser({...editingUser, isVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">تأیید شده</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingUser.isEmailVerified}
                            onChange={(e) => setEditingUser({...editingUser, isEmailVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">ایمیل تأیید شده</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingUser.isPhoneVerified}
                            onChange={(e) => setEditingUser({...editingUser, isPhoneVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">تلفن تأیید شده</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      لغو
                    </button>
                    <button
                      onClick={handleUpdateUser}
                      disabled={actionLoading === 'update'}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'update' ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          در حال ذخیره...
                        </>
                      ) : (
                        'ذخیره تغییرات'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4" dir="rtl">
            <div className="relative top-8 mx-auto max-w-2xl">
              <div className="bg-white rounded-xl shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                                  <h3 className="text-xl font-semibold text-gray-900">ایجاد کاربر جدید</h3>
              <p className="text-sm text-gray-500 mt-1">تکمیل اطلاعات کاربر</p>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                        <input
                          type="text"
                          value={newUser.firstName}
                          onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام خانوادگی</label>
                        <input
                          type="text"
                          value={newUser.lastName}
                          onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور</label>
                        <input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="اختیاری - برای حساب فقط OTP خالی بگذارید"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                        <input
                          type="text"
                          value={newUser.phoneNumber}
                          onChange={(e) => setNewUser({...newUser, phoneNumber: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">شهر</label>
                        <input
                          type="text"
                          value={newUser.city}
                          onChange={(e) => setNewUser({...newUser, city: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">استان</label>
                        <input
                          type="text"
                          value={newUser.province}
                          onChange={(e) => setNewUser({...newUser, province: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">کد پستی</label>
                        <input
                          type="text"
                          value={newUser.postalCode}
                          onChange={(e) => setNewUser({...newUser, postalCode: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام کسب و کار</label>
                        <input
                          type="text"
                          value={newUser.businessName}
                          onChange={(e) => setNewUser({...newUser, businessName: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">توضیحات کسب و کار</label>
                        <textarea
                          value={newUser.businessDescription}
                          onChange={(e) => setNewUser({...newUser, businessDescription: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">وب‌سایت کسب و کار</label>
                        <input
                          type="text"
                          value={newUser.businessWebsite}
                          onChange={(e) => setNewUser({...newUser, businessWebsite: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نوع کاربر / نقش</label>
                        <select
                          value={newUser.userType}
                          onChange={(e) => setNewUser({...newUser, userType: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="Individual">👤 شخصی</option>
                          <option value="Business">🏢 تجاری</option>
                          <option value="Admin">👑 ادمین</option>
                        </select>
                      </div>
                    </div>

                    {/* Status Checkboxes */}
                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">وضعیت حساب</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newUser.isActive}
                            onChange={(e) => setNewUser({...newUser, isActive: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">فعال</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newUser.isVerified}
                            onChange={(e) => setNewUser({...newUser, isVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">تأیید شده</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newUser.isEmailVerified}
                            onChange={(e) => setNewUser({...newUser, isEmailVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">ایمیل تأیید شده</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newUser.isPhoneVerified}
                            onChange={(e) => setNewUser({...newUser, isPhoneVerified: e.target.checked})}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label className="mr-2 block text-sm text-gray-900">تلفن تأیید شده</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      لغو
                    </button>
                    <button
                      onClick={handleCreateUser}
                      disabled={actionLoading === 'create'}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === 'create' ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          در حال ایجاد...
                        </>
                      ) : (
                        'ایجاد کاربر'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage; 
